import React, { useState, useEffect, useCallback } from "react"
import Typography from "aldoo-ra/Typography"
import { HistoryAPI } from "../history-api"
import {
  UserIcon,
  DocumentTextIcon,
  ChartBarIcon,
  TagIcon,
  PlusCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  ClockIcon
} from "@heroicons/react/24/outline"

/**
 * Stats Tab - Shows statistics and analytics
 */
const StatsTab = () => {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(false)

  // Load statistics
  const loadStatistics = useCallback(async () => {
    try {
      setLoading(true)

      const response = await HistoryAPI.GetStatistics()

      if (response.result) {
        setStats(response.statistics)
      }
    } catch (error) {
      console.error("Failed to load statistics:", error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadStatistics()
  }, [loadStatistics])

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center h-full">
        <Typography variant="admin_txt1_dim">
          Loading statistics...
        </Typography>
      </div>
    )
  }

  // Summary Card Component
  const SummaryCard = ({ title, value, icon: Icon, variant = "primary" }) => {
    const getIconColorClasses = () => {
      switch (variant) {
        case "success":
          return "text-admin_success"
        case "warning":
          return "text-admin_warning"
        case "error":
          return "text-admin_error"
        default:
          return "text-admin_primary"
      }
    }

    return (
      <div className="flex flex-col xl:flex-row items-center justify-between bg-admin_paper border border-admin_bordered rounded-xl p-6">
        <div className="flex items-center gap-3">
          <Icon className={`w-8 h-8 ${getIconColorClasses()}`} />
          <Typography
            component="output"
            variant="admin_h2"
          >
            {value.toLocaleString()}
          </Typography>
        </div>

        <Typography 
          variant="admin_txt1_dim" 
          className="text-center xl:text-right whitespace-nowrap"
        >
          {title}
        </Typography>
      </div>
    )
  }

  // List Card Component
  const ListCard = ({ title, items, icon: Icon, renderItem }) => (
    <div className="bg-admin_paper border border-admin_bordered rounded-xl p-6">

        <Typography 
          component="h2"
          variant="admin_h4"
          className="mb-6"
        >
          {title}
        </Typography>


      {items && items.length > 0 ? (
        <div className="divide-y divide-admin_bordered/30">
          {items.map((item, idx) => renderItem(item, idx))}
        </div>
      ) : (
        <Typography variant="admin_txt2_dim">
          No data available
        </Typography>
      )}
    </div>
  )

  return (
    <div className="h-screen bg-admin_canvas text-admin_text flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 bg-admin_paper border-b border-admin_bordered p-4">
        <div className="flex flex-col">
          <Typography 
            component="h1"
            variant="admin_h4"
          >
            Statistics
          </Typography>

          <Typography 
            variant="admin_body_dim" 
            className="my-0"
          >
            View analytics and insights about content changes and user activity
          </Typography>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_canvas p-6">
        <div className="space-y-6 pr-2">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <SummaryCard
          title="Total Entries"
          value={stats.totalEntries || 0}
          icon={DocumentTextIcon}
          variant="primary"
        />
        <SummaryCard
          title="Last 24 Hours"
          value={stats.recentActivity24h || 0}
          icon={ClockIcon}
          variant="success"
        />
        <SummaryCard
          title="Creates"
          value={stats.byAction?.create || 0}
          icon={PlusCircleIcon}
          variant="success"
        />
        <SummaryCard
          title="Updates"
          value={stats.byAction?.update || 0}
          icon={PencilSquareIcon}
          variant="warning"
        />
      </div>

      {/* Action Breakdown */}
      <div className="bg-admin_paper border border-admin_bordered rounded-xl p-6">
        <Typography 
          component="h2"
          variant="admin_h4" 
          className="mb-4"
        >
          Actions Breakdown
        </Typography>

        <div className="grid grid-cols-4 gap-4">
          <div className="text-center p-4 bg-admin_canvas rounded-lg">
            <PlusCircleIcon className="w-8 h-8 text-admin_success mx-auto mb-2" />

            <Typography variant="admin_h3" className="text-admin_success">
              {stats.byAction?.create || 0}
            </Typography>

            <Typography variant="admin_txt1_dim">Creates</Typography>
          </div>

          <div className="text-center p-4 bg-admin_canvas rounded-lg">
            <PencilSquareIcon className="w-8 h-8 text-admin_info mx-auto mb-2" />

            <Typography variant="admin_h3" className="text-admin_info">
              {stats.byAction?.update || 0}
            </Typography>

            <Typography variant="admin_txt1_dim">Updates</Typography>
          </div>

          <div className="text-center p-4 bg-admin_canvas rounded-lg">
            <TrashIcon className="w-8 h-8 text-admin_error mx-auto mb-2" />

            <Typography variant="admin_h3" className="text-admin_error">
              {stats.byAction?.delete || 0}
            </Typography>

            <Typography variant="admin_txt1_dim">Deletes</Typography>
          </div>

          <div className="text-center p-4 bg-admin_canvas rounded-lg">
            <TrashIcon className="w-8 h-8 text-admin_warning mx-auto mb-2" />

            <Typography variant="admin_h3" className="text-admin_warning">
              {stats.byAction?.['soft-delete'] || 0}
            </Typography>

            <Typography variant="admin_txt1_dim">Soft Deletes</Typography>
          </div>
        </div>
      </div>

      {/* Two Column Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Most Active Users */}
        <ListCard
          title="Most Active Users"
          items={stats.topUsers}
          icon={UserIcon}
          renderItem={(user, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-admin_primary flex items-center justify-center text-white text-sm font-bold">
                  {user.author?.charAt(0).toUpperCase() || "U"}
                </div>

                <Typography 
                  variant="admin_label" 
                  className="my-0"
                >
                  {user.author || "Unknown"}
                </Typography>
              </div>

              <Typography 
                component="output"
                variant="admin_h4" 
                className="text-admin_primary"
              >
                {user.changes}
              </Typography>
            </div>
          )}
        />

        {/* Most Changed Models */}
        <ListCard
          title="Most Changed Content"
          items={stats.byModel}
          icon={DocumentTextIcon}
          renderItem={(model, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
            >
              <div className="flex items-center gap-3">
                <DocumentTextIcon className="w-5 h-5 text-admin_text_secondary" />

                <Typography 
                  variant="admin_label" 
                  className="my-0"
                >
                  {model.model}
                </Typography>
              </div>

              <Typography 
                component="output"
                variant="admin_h4" 
                className="text-admin_primary"
              >
                {model.count}
              </Typography>
            </div>
          )}
        />
      </div>

      {/* Popular Tags */}
      {stats.topTags && stats.topTags.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ListCard
            title="Popular Tags"
            items={stats.topTags}
            icon={TagIcon}
            renderItem={(tag, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
              >
                <Typography variant="admin_body" className="my-0 font-medium">
                  {tag.tag}
                </Typography>
                <Typography variant="admin_h4" className="text-admin_primary">
                  {tag.count}
                </Typography>
              </div>
            )}
          />
        </div>
      )}

      {/* Tracked Models */}
      {stats.trackedModels && stats.trackedModels.length > 0 && (
        <div className="bg-admin_paper border border-admin_bordered rounded-xl p-6">
          <Typography variant="admin_h4" className="mb-4">
            Tracked Models ({stats.trackedModels.length})
          </Typography>

          <div className="flex flex-wrap gap-2">
            {stats.trackedModels.map((model, idx) => (
              <span
                key={idx}
                className="px-3 py-1.5 bg-admin_canvas rounded-lg text-sm font-medium"
              >
                {model}
              </span>
            ))}
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  )
}

export default StatsTab
